import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { DomainNotificationTemplateProviderContext, domainNotificationTemplateProviderReducer } from './internal'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import { convertNamedTemplateResponseToNotificationResponse } from '../../features/notifications/helpers/notification-helpers'
import { useService } from '@pushly/aqe/lib/hooks'
import { CustomApiResponseErrorHandler } from '../../types/api-call-options-with-custom-error-handler'
import { NotificationTemplateService } from '../../services/notification-template'

type NotificationProviderProps = {
    domainId: number
    templateId?: string | null
    onFetchError?: CustomApiResponseErrorHandler
    children: React.ReactNode
}

export const DomainNotificationTemplateProvider = (props: NotificationProviderProps) => {
    const { domainId, onFetchError, children } = props

    const propsTemplateId: number | null = 'templateId' in props && props.templateId ? Number(props.templateId) : null

    const tmplSvc = useService(NotificationTemplateService)

    const [state, dispatch] = useReducer(domainNotificationTemplateProviderReducer, {
        domainId,
        loading: !!props.templateId,
        templateId: props.templateId ?? null,
        template: null,
        notifications: [],
    })

    // prevents duplicate load effects without triggering re-renders
    const localLoaderInitializedRef = useRef(false)

    const loadRemoteNotification = useCallback(async () => {
        const canLoadRemote = !localLoaderInitializedRef.current || domainId !== state.domainId
        if (!canLoadRemote) return

        if (!propsTemplateId) return

        if (!localLoaderInitializedRef.current) localLoaderInitializedRef.current = true
        if (!state.loading) dispatch({ type: 'loading:set', loading: true })

        const notifications: GetNotificationResponse[] = []

        const { data: template } = await tmplSvc.fetchNamedNotificationTemplateById(domainId, propsTemplateId, {
            errorHandler: onFetchError,
        })

        if (template) {
            notifications.push(convertNamedTemplateResponseToNotificationResponse(template))
        }

        if (template && notifications.length) {
            dispatch({ type: 'notifications:set', notifications, template })
        }

        dispatch({ type: 'loading:set', loading: false })
    }, [domainId, propsTemplateId])

    useEffect(() => {
        loadRemoteNotification()
    }, [loadRemoteNotification])

    return (
        <DomainNotificationTemplateProviderContext.Provider value={{ state, dispatch }}>
            {children}
        </DomainNotificationTemplateProviderContext.Provider>
    )
}
